import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SidePanel } from "@/components/ui/side-panel";
import {
  AdminsKeys,
  createAdmin,
  getAdmin,
  updateAdmin,
} from "@/features/admins/api/admins";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
});

type FormValues = z.infer<typeof formSchema>;

interface NewAdminDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClearAdminSelection: () => void;
  selectedAdminId: string | null;
}

const NewAdminDrawer: React.FC<NewAdminDrawerProps> = ({
  open,
  setOpen,
  onClearAdminSelection,
  selectedAdminId,
}) => {
  const queryClient = useQueryClient();
  const [ticketUrl, setTicketUrl] = React.useState("");

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  // Get Admin
  const { data: selectedAdmin, isLoading: selectedAdminLoading } = useQuery({
    queryKey: [AdminsKeys.GET_ADMIN, selectedAdminId],
    enabled: !!selectedAdminId,
    queryFn: () => getAdmin(selectedAdminId as string),
  });

  // Create Admin
  const { mutate: createMutation, isPending: createPending } = useMutation({
    mutationFn: createAdmin,
    onSuccess: (data) => {
      setTicketUrl(data.ticketUrl);
      queryClient.invalidateQueries({ queryKey: [AdminsKeys.GET_ADMINS] });
      form.reset();
    },
  });

  // Update Admin
  const { mutate: updateMutation, isPending: updatePending } = useMutation({
    mutationFn: (data: FormValues) =>
      updateAdmin(selectedAdminId as string, {
        firstName: data.firstName,
        lastName: data.lastName,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AdminsKeys.GET_ADMINS] });
      onClearAdminSelection();
    },
  });

  // Populate form when editing
  React.useEffect(() => {
    if (selectedAdmin) {
      form.reset({
        firstName: selectedAdmin.firstName,
        lastName: selectedAdmin.lastName,
        email: selectedAdmin.email,
      });
    } else {
      form.reset({
        firstName: "",
        lastName: "",
        email: "",
      });
    }
  }, [selectedAdmin, form]);

  const onSubmit = (values: FormValues) => {
    if (selectedAdminId) {
      updateMutation(values);
    } else {
      createMutation(values);
    }
  };

  const isPending = createPending || updatePending;

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={onClearAdminSelection}
        disabled={isPending}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="admin-form"
        disabled={isPending || !form.formState.isValid}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {selectedAdminId ? "Saving..." : "Creating..."}
          </>
        ) : (
          <>{selectedAdminId ? "Save" : "Create"}</>
        )}
      </Button>
    </div>
  );

  return (
    <>
      <SidePanel
        open={open}
        onOpenChange={onClearAdminSelection}
        title={selectedAdminId ? "Edit Admin" : "Create New Admin"}
        subtitle={
          selectedAdminId
            ? "Update admin information"
            : "Add a new admin to the system"
        }
        footer={footerContent}
      >
        {selectedAdminLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <Form {...form}>
            <form
              id="admin-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter first name"
                        autoComplete="given-name"
                        disabled={selectedAdminLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter last name"
                        autoComplete="family-name"
                        disabled={selectedAdminLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Email <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter email address"
                        autoComplete="email"
                        disabled={!!selectedAdminId || selectedAdminLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        )}
      </SidePanel>

      <Dialog open={!!ticketUrl} onOpenChange={() => setTicketUrl("")}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Admin Created Successfully</DialogTitle>
            <DialogDescription>
              Admin has been created. Here's the ticket URL:
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-muted rounded-md">
            <p className="text-sm font-mono break-all">{ticketUrl}</p>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => {
                setTicketUrl("");
                setOpen(false);
              }}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NewAdminDrawer;
